import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { GeodataBoundaryPolygonModel } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { TokenService } from '../../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../../services/network/file-upload';
import { WebApi } from '../../../../services/web-api';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { errorUtils, translateApiErrors } from '../../../api/error-handling';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { Button } from '../../button/button';
import { DataTable, DataTableColumns, DataTableSortType } from '../../data-table/data-table';
import { Dialog } from '../../dialog/dialog';
import { FileSelectionIcon } from '../../file-selection-icon/file-selection-icon';
import { Icon } from '../../icon/icon';
import { IconPanel } from '../../icon-panel/icon-panel';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { BoundaryTypeIcon, StatusIcon } from '../boundary-polygon-icons';
import { GeodataContext } from '../geodata-state';
import { useBoundaryPolygons } from '../use-boundary-polygons';

export const supportedBoundaryPolygonFileTypes = '.kml,.kmz,.shp,.dxf,.dwg';

const BoundaryPolygonListContainer = styled.div``;

const NoBoundaryPolygonsPanelStyle = styled.div`
  display: flex;
  padding: 1em;
`;

type Item = GeodataBoundaryPolygonModel & {
  statusEnabledIcon: ReactNode;
  boundaryTypeIcon: ReactNode;
  actions: ReactNode;
};

interface DeleteConfirmationDialogProps {
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const DeleteConfirmationDialog = ({
  fileName,
  onConfirm,
  onCancel,
  loading = false,
}: DeleteConfirmationDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog onClose={onCancel}>
      {{
        header: t('boundaryPolygonList.confirmDeletion.header', { ns: 'cloudProcessing' }),
        content: (
          <p>
            {t('boundaryPolygonList.confirmDeletion.content', { ns: 'cloudProcessing', fileName })}
          </p>
        ),
        footer: (
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              color="error"
              disabled={loading}
              leftIcon={{ icon: loading ? ['fas', 'spinner'] : ['fas', 'trash-alt'] }}
              loading={loading}
              variant="contained"
              onClick={onConfirm}
            >
              {t('delete', { ns: 'common' })}
            </Button>
            <Button color="secondary" variant="text" onClick={onCancel}>
              {t('cancel', { ns: 'common' })}
            </Button>
          </Stack>
        ),
      }}
    </Dialog>
  );
};

const NoBoundaryPolygonsPanel = () => {
  const { t } = useTranslation();

  return (
    <NoBoundaryPolygonsPanelStyle>
      <IconPanel
        header={{ icon: { icon: ['fad', 'map'], opacity: '50%', size: '4x' } }}
        responsiveness={{ compressed: false }}
      >
        {{
          title: t('boundaryPolygonList.emptyState.title', { ns: 'cloudProcessing' }),
          info: t('boundaryPolygonList.emptyState.info', { ns: 'cloudProcessing' }),
        }}
      </IconPanel>
    </NoBoundaryPolygonsPanelStyle>
  );
};

// DataTable columns factory
const createColumns = (t: any): DataTableColumns<Item> => ({
  statusEnabledIcon: {
    title: t('boundaryPolygonList.table.status', { ns: 'cloudProcessing' }),
    alignment: 'center',
    width: '45px',
    sortableBy: { type: DataTableSortType.STRING, columnKey: 'status' },
    unfilterable: true,
  },
  boundaryTypeIcon: {
    title: t('boundaryPolygonList.table.type', { ns: 'cloudProcessing' }),
    alignment: 'center',
    width: '45px',
    sortableBy: { type: DataTableSortType.STRING, columnKey: 'boundaryType' },
    unfilterable: true,
  },
  fileName: {
    sortableBy: { type: DataTableSortType.STRING },
    unfilterable: true,
    title: t('name', { ns: 'common' }),
  },
  actions: {
    alignment: 'center',
    width: '45px',
    unfilterable: true,
  },
});

const tableStyle = {
  rowCell: {
    fontSize: '14px',
  },
  headerCell: {
    fontSize: '14px',
  },
};

export const BoundaryPolygonList = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isGeodataProcessing, geodata } = useContext(GeodataContext);
  const { handleError } = useErrorHandling();

  const {
    boundaryPolygons,
    refetch: refetchBoundaryPolygons,
    isPending: isLoadingBoundaryPolygons,
  } = useBoundaryPolygons(geodata?.id || '');
  const [searchText, setSearchText] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const deleteDialog = useDialog();
  const [deleteItem, setDeleteItem] = useState<{ fileName: string; id: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const onFilesSelected = useCallback(
    async (files: File[]) => {
      if (!geodata) {
        console.error('No geodata available');
        return;
      }

      for (const file of files) {
        const fileKey = `${file.name}_${file.size}_${file.lastModified}`;

        try {
          // Add file to uploading state
          setUploadingFiles((prev) => new Set(prev).add(fileKey));

          // Upload the file first
          const upload = new FileUpload({
            apiBaseUrl: WebApi.getUrl(),
            authToken: new TokenService().get(),
            file: file,
            concurrency: 1,
          });

          const { id: uploadId } = await upload.start();

          // Create the boundary polygon record
          await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().createBoundaryPolygon({
            path: {
              geodataId: geodata.id,
            },
            body: {
              uploadId: uploadId,
              boundaryType: 'Outer',
              fileName: file.name,
            },
          });

          // Refresh the boundary polygons list
          await refetchBoundaryPolygons();

          // Show success message
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'success',
            message: t('boundaryPolygonList.messages.uploadSuccess', {
              ns: 'cloudProcessing',
              fileName: file.name,
            }),
          });
        } catch (error) {
          console.error('Failed to upload boundary polygon file:', error);
          handleError(error);
          const apiError = errorUtils.toApiErrorResponse(error);
          if (apiError) {
            const translatedErrors = translateApiErrors(apiError);
            for (const translatedError of translatedErrors) {
              publish(SubscriptionTopic.ToastrMessage, {
                type: 'error',
                message: translatedError.errorMessage,
              });
            }
          } else {
            // If error is not an API error, show generic error message
            publish(SubscriptionTopic.ToastrMessage, {
              type: 'error',
              message: t('unexpectedError', { ns: 'common' }),
            });
          }
        } finally {
          // Remove file from uploading state
          setUploadingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fileKey);
            return newSet;
          });
        }
      }
    },
    [geodata, handleError, refetchBoundaryPolygons, t],
  );

  const handleToggleEnabled = useCallback(
    async (id: string) => {
      if (!geodata) {
        console.error('No geodata available');
        return;
      }

      const item = boundaryPolygons.find((bp) => bp.id === id);
      if (!item) {
        console.error('Boundary polygon not found');
        return;
      }

      try {
        const response = await SkyMapAxiosServiceFactory.instance
          .createGeodataServiceV0()
          .patchBoundaryPolygon({
            path: {
              geodataId: geodata.id,
              boundaryPolygonId: id,
            },
            body: {
              enabled: !item.enabled,
            },
          });

        // Refresh the boundary polygons list
        await refetchBoundaryPolygons();

        // Show success message
        publish(SubscriptionTopic.ToastrMessage, {
          type: 'success',
          message: response.data.enabled
            ? t('boundaryPolygonList.messages.enabled', {
                ns: 'cloudProcessing',
                fileName: item.fileName,
              })
            : t('boundaryPolygonList.messages.disabled', {
                ns: 'cloudProcessing',
                fileName: item.fileName,
              }),
        });
      } catch (error) {
        console.error('Failed to toggle boundary polygon enabled status:', error);
        handleError(error);
      }
    },
    [geodata, boundaryPolygons, handleError, t, refetchBoundaryPolygons],
  );

  const handleToggleBoundaryType = useCallback(
    async (id: string) => {
      if (!geodata) {
        console.error('No geodata available');
        return;
      }

      const item = boundaryPolygons.find((bp) => bp.id === id);
      if (!item) {
        console.error('Boundary polygon not found');
        return;
      }

      const newBoundaryType = item.boundaryType === 'Inner' ? 'Outer' : 'Inner';

      try {
        await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().patchBoundaryPolygon({
          path: {
            geodataId: geodata.id,
            boundaryPolygonId: id,
          },
          body: {
            boundaryType: newBoundaryType,
          },
        });

        // Refresh the boundary polygons list
        await refetchBoundaryPolygons();

        // Show success message
        publish(SubscriptionTopic.ToastrMessage, {
          type: 'success',
          message: t('boundaryPolygonList.messages.typeChanged', {
            ns: 'cloudProcessing',
          }),
        });
      } catch (error) {
        console.error('Failed to toggle boundary polygon type:', error);
        handleError(error);
        const apiError = errorUtils.toApiErrorResponse(error);
        if (apiError) {
          const translatedErrors = translateApiErrors(apiError);
          for (const translatedError of translatedErrors) {
            publish(SubscriptionTopic.ToastrMessage, {
              type: 'error',
              message: translatedError.errorMessage,
            });
          }
        } else {
          // If error is not an API error, show generic error message
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'error',
            message: t('unexpectedError', { ns: 'common' }),
          });
        }
      }
    },
    [geodata, boundaryPolygons, handleError, t, refetchBoundaryPolygons],
  );

  const handleDeleteClick = useCallback(
    (id: string) => {
      const item = boundaryPolygons.find((bp) => bp.id === id);
      if (!item) {
        console.error('Boundary polygon not found');
        return;
      }

      setDeleteItem({
        fileName: item.fileName,
        id: id,
      });
      deleteDialog.show();
    },
    [boundaryPolygons, deleteDialog],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!geodata || !deleteItem) {
      console.error('No geodata or delete item available');
      return;
    }

    const { id, fileName } = deleteItem;

    try {
      setIsDeleting(true);
      await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().deleteBoundaryPolygon({
        path: {
          geodataId: geodata.id,
          boundaryPolygonId: id,
        },
      });

      // Refresh the boundary polygons list
      await refetchBoundaryPolygons();

      // Show success message
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('boundaryPolygonList.messages.deleteSuccess', {
          ns: 'cloudProcessing',
          fileName,
        }),
      });
    } catch (error) {
      console.error('Failed to delete boundary polygon:', error);
      handleError(error);
      const apiError = errorUtils.toApiErrorResponse(error);
      if (apiError) {
        const translatedErrors = translateApiErrors(apiError);
        for (const translatedError of translatedErrors) {
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'error',
            message: translatedError.errorMessage,
          });
        }
      } else {
        // If error is not an API error, show generic error message
        publish(SubscriptionTopic.ToastrMessage, {
          type: 'error',
          message: t('unexpectedError', { ns: 'common' }),
        });
      }
    } finally {
      setIsDeleting(false);
      // Close dialog and clear delete item
      deleteDialog.hide();
      setDeleteItem(null);
    }
  }, [geodata, deleteItem, handleError, refetchBoundaryPolygons, deleteDialog, t]);

  // Filter items based on search text
  const filteredItems = useMemo(() => {
    const filtered = boundaryPolygons
      .filter((item) => item.fileName.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.id.localeCompare(b.id));

    return filtered.map((model) => ({
      ...model,
      statusEnabledIcon: <StatusIcon model={model} t={t} onToggleEnabled={handleToggleEnabled} />,
      boundaryTypeIcon: (
        <BoundaryTypeIcon model={model} t={t} onToggleBoundaryType={handleToggleBoundaryType} />
      ),
      actions: (
        <Icon
          color={theme.color.gray.medium}
          icon={['fas', 'trash-alt']}
          key={v4()}
          style={{ cursor: 'pointer' }}
          title={t('boundaryPolygonList.actions.delete', { ns: 'cloudProcessing' })}
          onClick={() => handleDeleteClick(model.id)}
        />
      ),
    })) as Item[];
  }, [
    boundaryPolygons,
    searchText,
    theme,
    t,
    handleToggleEnabled,
    handleToggleBoundaryType,
    handleDeleteClick,
  ]);

  // Check if any files are currently uploading
  const isUploading = uploadingFiles.size > 0;
  const renderNoBoundaryPolygonsPanel = !isLoadingBoundaryPolygons && boundaryPolygons.length === 0;

  return (
    <>
      <OverlayLoader visible={isLoadingBoundaryPolygons}>
        <BoundaryPolygonListContainer>
          <Stack spacing={1}>
            <Stack alignItems="center" direction="row" spacing={0.5}>
              <TextBox
                placeholder={t('search', { ns: 'common' })}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <FileSelectionIcon
                accept={supportedBoundaryPolygonFileTypes}
                color={theme.color.gray.dark}
                disabled={isGeodataProcessing || isUploading}
                fixedWidth={true}
                icon={isUploading ? ['fas', 'spinner'] : ['fad', 'file-upload']}
                multiple={true}
                spin={isUploading}
                title={
                  isUploading
                    ? t('boundaryPolygonList.upload.uploading', { ns: 'cloudProcessing' })
                    : t('boundaryPolygonList.upload.title', { ns: 'cloudProcessing' })
                }
                onFilesSelected={onFilesSelected}
                onHoverStyle={{ icon: isUploading ? ['fas', 'spinner'] : ['fas', 'file-upload'] }}
              />
              <Icon
                color={theme.color.gray.dark}
                icon={['fad', 'refresh']}
                spin={isLoadingBoundaryPolygons}
                style={{ cursor: 'pointer' }}
                title={t('refreshList', { ns: 'common' })}
                onClick={() => refetchBoundaryPolygons()}
              />
            </Stack>

            {renderNoBoundaryPolygonsPanel ? (
              <NoBoundaryPolygonsPanel />
            ) : (
              <DataTable
                columns={createColumns(t)}
                fixedLayout={true}
                items={filteredItems}
                pageSize={10}
                tableStyle={tableStyle}
              />
            )}
          </Stack>
        </BoundaryPolygonListContainer>
      </OverlayLoader>

      {deleteDialog.render(
        deleteItem && (
          <DeleteConfirmationDialog
            fileName={deleteItem.fileName}
            loading={isDeleting}
            onCancel={() => {
              deleteDialog.hide();
              setDeleteItem(null);
            }}
            onConfirm={handleDeleteConfirm}
          />
        ),
      )}
    </>
  );
};
